import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  hasError: boolean;
  error?: Error;
}

/** Generic error boundary with a recovery action. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep secrets out of logs; report only the component stack.
    console.error("UI error boundary caught:", error.message, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-base font-medium text-fg">Something went wrong</p>
          <p className="max-w-md text-sm text-fg-mute">{this.state.error?.message}</p>
          <Button variant="secondary" onClick={() => this.setState({ hasError: false, error: undefined })}>
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Catches code-split chunk load failures (stale deploys) and offers a refresh
 * instead of a permanent white screen (PRD 6.3).
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error): void {
    console.error("Chunk load error:", error.message);
  }

  isChunkError(): boolean {
    const msg = this.state.error?.message ?? "";
    return /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i.test(msg);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-base font-medium text-fg">
            {this.isChunkError() ? "A new version is available" : "Something went wrong"}
          </p>
          <p className="max-w-md text-sm text-fg-mute">
            {this.isChunkError() ? "Refresh to load the latest version." : this.state.error?.message}
          </p>
          <Button onClick={() => window.location.reload()}>Refresh</Button>
        </div>
      );
    }
    return this.props.children;
  }
}
